import type React from "react";
import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { BasicRichText } from "./BasicRichText";
import { useGetMentions } from "./getMentions";
import SimplePreset from "./presets/simple/simple";

const mentionRegex =
  /\(([@#]|\$[a-z]+)\)\[((?:[.a-z,!?<> '_#@0-9\u00C0-\u024F]|-|\(|\))+)\]\(_id:([a-z0-9#]+)\)/gim;

/**
 * Convert editor HTML into the discussion wire format used by comment APIs:
 * `(@)[Label](_id:…)` / `($ideas)[Label](_id:…)`.
 */
const htmlToMentionWireFormat = (html: string): string => {
  if (!html || html === "<p></p>") {
    return "";
  }

  const doc = new DOMParser().parseFromString(html, "text/html");

  const serializeNode = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent ?? "";
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return "";
    }

    const el = node as HTMLElement;
    if (el.tagName === "A" && el.hasAttribute("data-mention-atom-id")) {
      const id = el.getAttribute("data-mention-atom-id") ?? "";
      const name = el.getAttribute("data-mention-atom-name") ?? "";
      const label = el.textContent ?? "";
      if (name === "users") {
        return `(@)[${label}](_id:${id})`;
      }
      if (name) {
        return `($${name})[${label}](_id:${id})`;
      }
      return label;
    }

    if (el.tagName === "BR") {
      return "\n";
    }

    return Array.from(el.childNodes).map(serializeNode).join("");
  };

  return Array.from(doc.body.childNodes).map(serializeNode).join("\n").trim();
};

const getMentionCaptureGroups = (text: string) => {
  const executedRegex = mentionRegex.exec(text);
  if (!executedRegex || executedRegex.length < 4) return {};
  return { fullName: executedRegex[2], _id: executedRegex[3] };
};

const insertionString = "<?insert!>";

const replaceMentions = (text: string) => {
  if (!text) return "";
  return text.replaceAll(
    mentionRegex,
    `<a target="_blank" class="mention mention-users" rel="noopener noreferrer nofollow" data-mention-atom-id="$3" data-mention-atom-name="users">$2</a>`,
  );
};

const hasExternallyInsertedMention = (text: string) =>
  text.indexOf("(@)") !== -1 && text.endsWith(insertionString);

type MentionTextProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  editorRef?: React.RefObject<unknown>;
};

export const MentionText = ({
  value,
  onChange,
  placeholder,
  editorRef,
}: MentionTextProps) => {
  const [mentions, loadMentions] = useGetMentions();
  const [externalValue, setExternalValue] = useState(
    replaceMentions(value) || "",
  );
  const [currentValue, setCurrentValue] = useState(
    replaceMentions(value) || "",
  );
  const [currentHTML, setCurrentHTML] = useState(
    replaceMentions(value) || "<p></p>",
  );

  const { t } = useTranslation();

  useEffect(() => {
    if (value === "") {
      setCurrentValue("");
      setCurrentHTML("<p></p>");
    }

    if (
      !currentValue.endsWith("@") &&
      !externalValue.endsWith("@") &&
      value.endsWith("@")
    ) {
      const replace =
        currentValue.endsWith(" ") || currentValue.length === 0
          ? "@</p>"
          : " @</p>";
      setCurrentHTML(currentHTML.replace(/<\/p>$/, replace));
    } else if (
      !hasExternallyInsertedMention(currentValue) &&
      !hasExternallyInsertedMention(externalValue) &&
      hasExternallyInsertedMention(value)
    ) {
      const replacedInsertionString = value.replace(insertionString, "");
      const mentionCaptureGroup = getMentionCaptureGroups(value);
      const replacedMention = replacedInsertionString.replace(
        mentionRegex,
        `<a target="_blank" class="mention mention-users" rel="noopener noreferrer nofollow" data-mention-atom-id="${mentionCaptureGroup._id}" data-mention-atom-name="users">${mentionCaptureGroup.fullName}</a>`,
      );
      setCurrentValue(replacedInsertionString);
      onChange(replacedInsertionString);
      setCurrentHTML(
        currentHTML.replace(
          "</p>",
          `${(!currentValue || currentValue.endsWith(" ") ? "" : " ") + replacedMention}</p>`,
        ),
      );
    }
    setExternalValue(value);
  }, [currentValue, externalValue, currentHTML, value, onChange]);

  return (
    <BasicRichText
      placeholder={placeholder || t("mention.guidance")}
      value={currentHTML}
      onChange={(newValue) => {
        setCurrentHTML(newValue);
        const constructed = htmlToMentionWireFormat(newValue);
        setCurrentValue(constructed);
        onChange(constructed);
      }}
      mentions={mentions}
      onSuggest={(kind, query) => loadMentions(kind, query)}
      capabilities={SimplePreset}
      hideMenu
      disableBorder
      editorRef={editorRef}
    />
  );
};
